import { useLocalizedPath } from "../i18n/useLocalizedPath";
import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  Flex,
  Button,
  Badge,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import SEO from "./SEO";

interface ExploreItem {
  id: string;
  emoji: string;
  titleKey: string;
  descKey: string;
  tagKey: string;
  tagColor: string;
  path: string;
  gradient: string;
  borderColor: string;
}

const EXPLORE_ITEMS: ExploreItem[] = [
  {
    id: "zodiac",
    emoji: "✨",
    titleKey: "explore.items.zodiac.title",
    descKey: "explore.items.zodiac.desc",
    tagKey: "explore.items.zodiac.tag",
    tagColor: "purple",
    path: "/zodiac",
    gradient: "linear(to-br, purple.50, pink.50)",
    borderColor: "purple.200",
  },
  {
    id: "hexaco",
    emoji: "🧭",
    titleKey: "explore.items.hexaco.title",
    descKey: "explore.items.hexaco.desc",
    tagKey: "explore.items.hexaco.tag",
    tagColor: "teal",
    path: "/hexaco",
    gradient: "linear(to-br, teal.50, blue.50)",
    borderColor: "teal.200",
  },
  // 之後可以在這裡加更多項目，例如：
  // { id: "mbti", emoji: "🧠", titleKey: "explore.items.mbti.title", ... }
];

const Explore: React.FC = () => {
  const { t } = useTranslation();
  const localizePath = useLocalizedPath();

  return (
    <>
      <SEO
        title={t("explore.seo.title")}
        description={t("explore.seo.description")}
        keywords={t("explore.seo.keywords")}
        canonical="https://deepsoulai.com/explore"
      />

      {/* Hero Banner */}
      <Box
        bgGradient="linear(to-br, blue.50, purple.50, pink.50)"
        pt={{ base: 10, md: 14 }}
        pb={{ base: 8, md: 10 }}
      >
        <Container maxW="1100px">
          <VStack spacing={3} textAlign="center">
            <Text fontSize={{ base: "3xl", md: "4xl" }}>🌟</Text>
            <Heading
              as="h1"
              size={{ base: "xl", md: "2xl" }}
              bgGradient="linear(to-r, blue.500, purple.500)"
              bgClip="text"
              fontWeight="extrabold"
            >
              {t("explore.title")}
            </Heading>
            <Text
              color="gray.600"
              maxW="500px"
              fontSize={{ base: "sm", md: "md" }}
              lineHeight="tall"
            >
              {t("explore.subtitle")}
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Content Grid */}
      <Box bg="gray.50" minH="60vh" py={{ base: 8, md: 12 }}>
        <Container maxW="1100px">
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={6}>
            {EXPLORE_ITEMS.map((item) => (
              <Box
                key={item.id}
                bg="white"
                borderRadius="2xl"
                overflow="hidden"
                boxShadow="sm"
                border="1px solid"
                borderColor="gray.100"
                transition="all 0.25s ease"
                _hover={{
                  transform: "translateY(-4px)",
                  boxShadow: "lg",
                  borderColor: item.borderColor,
                }}
              >
                {/* Card Top Gradient Banner */}
                <Box
                  bgGradient={item.gradient}
                  px={6}
                  pt={6}
                  pb={5}
                >
                  <Flex justify="space-between" align="flex-start" mb={3}>
                    <Text fontSize="3xl">{item.emoji}</Text>
                    <Badge
                      colorScheme={item.tagColor}
                      borderRadius="full"
                      px={3}
                      py={1}
                      fontSize="xs"
                      fontWeight="semibold"
                    >
                      {t(item.tagKey)}
                    </Badge>
                  </Flex>
                  <Heading size="md" color="gray.800" mb={1}>
                    {t(item.titleKey)}
                  </Heading>
                </Box>

                {/* Card Body */}
                <VStack align="stretch" spacing={4} px={6} py={5}>
                  <Text fontSize="sm" color="gray.600" lineHeight="1.8">
                    {t(item.descKey)}
                  </Text>
                  <Button
                    size="sm"
                    colorScheme={item.tagColor}
                    variant="solid"
                    borderRadius="full"
                    alignSelf="flex-start"
                    px={5}
                    as={RouterLink}
                    to={localizePath(item.path)}
                    aria-label={t(item.titleKey)}
                  >
                    {t("explore.viewMore")}
                  </Button>
                </VStack>
              </Box>
            ))}

            {/* Coming Soon placeholder */}
            <Box
              bg="white"
              borderRadius="2xl"
              overflow="hidden"
              boxShadow="sm"
              border="1px dashed"
              borderColor="gray.200"
              opacity={0.6}
            >
              <Box bgGradient="linear(to-br, gray.50, gray.100)" px={6} pt={6} pb={5}>
                <Flex justify="space-between" align="flex-start" mb={3}>
                  <Text fontSize="3xl">🔮</Text>
                  <Badge
                    borderRadius="full"
                    px={3} py={1}
                    fontSize="xs"
                    fontWeight="semibold"
                    colorScheme="gray"
                  >
                    {t("explore.comingSoonTag")}
                  </Badge>
                </Flex>
                <Heading size="md" color="gray.400" mb={1}>
                  {t("explore.comingSoonTitle")}
                </Heading>
              </Box>
              <VStack align="stretch" spacing={4} px={6} py={5}>
                <Text fontSize="sm" color="gray.400" lineHeight="1.8">
                  {t("explore.comingSoonDesc")}
                </Text>
              </VStack>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>
    </>
  );
};

export default Explore;

